import { Component, Vue, Watch } from 'vue-property-decorator';
import { Model as DataModel } from '@/models/Model';
import { User as UserModel } from '@/models/User';
import ErrorHandler from '@/support/ErrorHandler';
import { AxiosError } from 'axios';
import { cloneDeep, isArray } from 'lodash';
import { styles } from '@/support/Style';
import { Avatar } from '@/support/Avatar';
import { Address } from '@/models/Address';
import LoaderCard from '@/components/loader-card/LoaderCard';
import MiPassword, { MiPasswordVisibility, MiPasswordIcons } from '@/components/mi-password/MiPassword';
import { InstanceLoader } from '@/support/InstanceLoader';
import { filtered_country_list } from '@/support/Countries';
import QuizAnswers from '@/components/quiz-anwsers/QuizAnswers';
import { LeadFile } from '@/models/LeadFile';
import { Lead } from '@/models/Lead';
import { Contact } from '@/models/Contact';
import { formatDate, copyStringToClipboard, currency } from '@/support/String';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Order } from '@/models/Order';
import { DateTime } from 'luxon';
import { UserFile } from '@/models/UserFile';
import OrderDataTable from '@/views/Users/OrderDataTable';
import ReferralCreditLogDatatable from '@/views/Users/ReferralCreditLogDatatable';
import VueTelInput from 'vue-tel-input';
import ItemTable from '@/components/item-table/ItemTable';
import { StyledBoxOptions, styledBoxOptions } from '@/support/StyledBox';
import { StatusOptions, statusOptions } from '@/support/Status';
import Mfa from '@/components/mfa/Mfa';
import RequestsTab from '@/views/Requests/RequestsTab/RequestsTab.vue';
import RequestTab from '@/views/Requests/RequestTab/RequestTab.vue';
import { RoleEnum } from '@/models/User';
import { BoxRequest } from '@/models/BoxRequest';
import { languageOptions } from '@/support/Languages';
import UserStatus from '@/support/UserStatus';
import i18n from '@/i18n';


// @TODO the whole must edit / can finish stuff is all over the place... put in one object

@Component<User>({
  components: {
    ItemTable,
    LoaderCard,
    Mfa,
    MiPassword,
    OrderDataTable,
    QuizAnswers,
    'requests-tab': RequestsTab,
    'request-tab': RequestTab,
    VueTelInput,
    ReferralCreditLogDatatable,
  },
  filters: {
    formatTimeSeconds(time: string) {
      if (!time) {
        return;
      }
      return formatDate(time, 'dd-LL-yyyy HH:mm');
    },
    formatBirthday(date: string) {
      if (!date) {
        return '';
      }

      const birthday = DateTime.fromFormat(date, 'yyyy-LL-dd');
      if (!birthday.isValid) {
        return date;
      }

      const now = DateTime.local();
      return Math.floor(Math.abs(birthday.diff(now, 'years').toObject().years || 0));
    },
  },
})
export default class User extends Vue {
  public $pageTitle = 'User';
  protected user: DataModel = new DataModel();
  protected userFile: DataModel = new DataModel();
  protected userClone: DataModel | null = null;
  protected address: Address | null = null;
  protected paazlOrders: paazlOrder[] = [];
  private type: string | null = null;
  protected currentOrders: Order[] | null = null;

  protected isEditingProfile: boolean = false;
  protected isEditingRepeat: boolean = false;
  protected isEditingRepeatFrequency: boolean = false;
  protected isEditingAddress: boolean = false;
  protected isEditingSettings: boolean = false;

  protected isEditingNotes: boolean = false;
  protected isUpdatingNotes: boolean = false;
  protected isSavedNotes: boolean = false;

  // Admin note
  protected isEditingNote: boolean = false;
  protected isUpdatingNote: boolean = false;
  protected userAdminOrPlannerNote: string = '';
  protected isSavedNote: boolean = false;

  protected isChangingPassword: boolean = false;
  protected isLoadingCountries: boolean = false;
  protected fullscreenInbox: boolean = false;
  protected isAddingExtraAddress: boolean = false;
  protected isOpeningQRDialog: boolean = false;

  protected isLoading: boolean = true;
  protected isUpdating: boolean = false;
  protected isSaved: boolean = false;
  protected isLoadingButton: boolean = false;
  protected isSavedButton: boolean = false;
  protected copyLinkText: string = 'Kopieer stijlquiz link';

  protected isPasswordValid: boolean = false;
  protected activeTab: string = 'stijlquiz';

  protected stijlquizAnswers: any[] = [];
  protected stijlquizImages: any[] = [];

  protected phone: string = '';
  protected phoneValid: boolean = false;

  protected languageOptions = languageOptions;

  protected birthdayDatepicker: boolean = false;
  protected repeatIntentionDatepicker: boolean = false;
  protected repeatDatepicker: boolean = false;

  protected latestBoxRequest: BoxRequest | null = null;

  protected selectedRequestId = '';

  protected userNotes = '';

  protected RoleEnum = RoleEnum;

  private inProgress: BoxRequest | null = null;

  protected frequencyOptions: Record<string, any>[] = [
    { text: '2 maanden', value: '2M' },
    { text: '3 maanden', value: '3M' },
    { text: '6 maanden', value: '6M' },
    { text: 'Geen', value: null },
  ];

  protected repeatOrderOption = '';

  protected iterationKey = 0; // to update the language in quiz answers after changing

  protected repeatOrderOptions: Record<string, any>[] = [
    { text: 'Geen interesse', value: 'no' },
    { text: 'Niet gepland, geen reminder', value: 'not_scheduled_no_reminder' },
    { text: 'Niet gepland, wel reminders vanaf', value: 'not_scheduled_has_reminder' },
    { text: 'Gepland op', value: 'scheduled' },
  ];

  public statusses: string[] = ['active', 'blacklist', 'discontinued', 'open_invoice'];
  // public statusses: object[] = [{
  //   name: 'lead',
  //   id: 'lead',
  // }, {
  //   name: 'active',
  //   id: 'active'
  // }, {
  //   name: 'blacklisted',
  //   id: 'blacklisted'
  // }, {
  //   name: 'discontinued',
  //   id: 'discontinued'
  // }]

  protected userFinishedAddressEdit: boolean = false;
  public get canFinishOrder(): boolean {
    // if user must edit, he can't finish.
    return !this.userMustEditAddress || this.userFinishedAddressEdit;
  }

  /**
   * currentOrderId
   *
   * coming from OrderOverview.ts
   * if user tries to finish order while address is not complete
   * the user is send back to personal edit page.
   * The string is the order number.
   *
   * @protected
   * @type {string}
   * @memberof User
   */
  protected currentOrderId: string = '';

  /**
   * userMustEditAddress
   * is dependend on currentOrderId
   * it's the related bool.
   *
   * @protected
   * @type {boolean}
   * @memberof User
   */
  protected userMustEditAddress: boolean = false;

  protected dateFormatted: string = '';

  protected includes: { [key: string]: string[] } = {
    Leads: ['token', 'rights'],
    Contacts: [
      'profile',
      'answers',
      'paazl',
      'orders',
      'rights',
      'attachments',
      'referral_credit_logs',
      'referral_code_order',
    ],
    Stylists: ['token'],
    Admin: [],
    Support: [],
  };

  protected images = {
    logo: '',
    invoice: '',
    login: '',
  };

  protected passwordVisibility: MiPasswordVisibility = {
    ruleList: false,
    showPassword: false,
  };

  protected icons: MiPasswordIcons = {
    password: 'lock_open',
    confirmation: 'lock_open',
  };

  public mounted() {
    this.$store.dispatch('changeBreadcrumbs', [{ name: this.type, path: `/${this.type}` }]);
    // @ts-ignore
    this.type = this.$route.meta.type;
    // @ts-ignore
    this.user = InstanceLoader.get(this.$route.meta.model);

    if (this.type === 'Leads') {
      this.userFile = InstanceLoader.get('LeadFile');
    } else if (this.type === 'Contacts') {
      this.userFile = InstanceLoader.get('UserFile');
    }
    this.initialize();

    // orderOverview has authority over user.... can demand address info
    this.currentOrderId = this.$route.query.hasOwnProperty('currentOrderId')
      ? String(this.$route.query.currentOrderId)
      : '';

    this.userMustEditAddress = this.currentOrderId.length !== 0; // orderNo is given by orderoverview

    // If the user must edit, he is editing.
    if (this.userMustEditAddress) this.isEditingProfile = true;

    this.checkAddressMinimumRequirementsMet();
  }

  protected checkAddressMinimumRequirementsMet(): void {
    const reqInputs: HTMLInputElement[] = Array.from(
      document.querySelectorAll('.minimum-required-address-fields input'),
    );
    const reqInputsNotFilledIn = reqInputs.filter(input => !input.value);

    if (!reqInputsNotFilledIn.length) {
      this.userFinishedAddressEdit = true;
    }
  }

  protected parsedStatus(status: string) {
    return status.replace(' ', '_').toLowerCase();
  }

  protected get isEditingCurrentOrder() {
    return this.$route.query.editing;
  }

  protected finishOrder() {
    if (this.canFinishOrder) {
      this.$router.push(`/webshop/order/${this.$route.query.currentOrderId}/order-overview`);
    }
  }

  protected getFrequencyLabel(key: string) {
    const indexedFrequency = this.frequencyOptions.find((frequency: Record<string, any>) => {
      return frequency.value === key;
    });

    if (!indexedFrequency) {
      return '';
    }

    return indexedFrequency.text;
  }

  protected getOpenForNextBoxLabel(key: string) {
    const indexedOption = this.repeatOrderOptions.find((option: Record<string, any>) => {
      return option.value === key;
    });

    if (!indexedOption) {
      return '';
    }

    return indexedOption.text;
  }

  public async initialize() {
    if (!this.type) {
      return;
    }

    await this.user
      .include(this.includes[this.type])
      .find(this.$route.params.id)
      .then(async (user: DataModel) => {
        this.user = user;
        if (this.user && (this.user as any).address) {
          this.address = new DataModel().dataToModel((this.user as any).address, 'Address');
        }

        if (this.type === 'Leads') {
          if ((this.user as Lead).answers.length) {
            (this.user as Lead).answers = JSON.parse((this.user as Lead).answers);
          } else {
            (this.user as Lead).answers = '';
          }
          this.$store.dispatch('updateLeadAnswers', this.user);
          this.stijlquizAnswers = (this.user as Lead).answers;
          this.fetchFiles();
          this.dateFormatted = formatDate((this.user as Lead).birthday_at as string);
        }

        if (this.type === 'Contacts') {
          if ((this.user as any).note) {
            this.userAdminOrPlannerNote = (this.user as any).note;
          }

          if (((this.user as any).profile as any).note) {
            this.userNotes = (this.user as any).profile.note;
          }

          if (((this.user as any).profile as any).answers) {
            (this.user as any).profile.answers = JSON.parse(((this.user as any).profile as any).answers);
          }

          if (((this.user as any).profile as any).paazl) {
            this.paazlOrders = ((this.user as any).profile as any).paazl;
          }

          if (((this.user as any).profile as any).answers) {
            (this.user as any).profile.answers = [...((this.user as any).profile as any).answers];
          }

          if (((this.user as any).profile as any).answers) {
            this.stijlquizAnswers = [...((this.user as any).profile as any).answers];
          }

          this.$store.dispatch('updateLeadAnswers', this.user as any);
          this.fetchOrders();
          this.fetchFiles();
          this.dateFormatted = formatDate((this.user as any).birthday_at as string);
          await this.fetchLatestRequestBox();
        }

        this.isLoading = false;
        this.initBreadcrumb();
      })
      .catch((error: AxiosError) => {
        console.log(error);
        ErrorHandler.network(error);
      });

    // new UserModel()
    // .include(['contact', 'address', 'country', 'roles'])
    // .find(this.$route.params.id)
    // .then((user: UserModel) => {
    //   this.user = user;
    //   if(this.user && this.user.address) {
    //     this.address = new DataModel().dataToModel(this.user.address, 'Address');
    //   }

    //   this.isLoading = false;
    //   this.initBreadcrumb();
    // })
    // .catch((error: AxiosError) => {
    //   ErrorHandler.network(error);
    // });
  }

  protected async updateLatestBoxRequestAnswers(answers: any): Promise<void> {
    if (!this.latestBoxRequest) {
      return;
    }
    if (!this.latestBoxRequest.info) {
      return;
    }

    this.latestBoxRequest.info.forEach(item => {
      answers.forEach((currentAnswer: any) => {
        if (item.key === 'Box voorkeuren') {
          if (currentAnswer.question.key === 'textarea_1' && currentAnswer.question.title === 'Box voorkeuren') {
            item.value = currentAnswer.value;
          }
        }

        if (currentAnswer.question.questionText === item.key) {
          item.value = currentAnswer.value;
        }
      });
    });

    try {
      await this.latestBoxRequest.update();
    } catch (error) {
      // Handle error
      console.log('error: ', error);
      return;
    }
  }

  protected async fetchLatestRequestBox(): Promise<void> {
    if (!this.user) {
      return;
    }

    const userObj = this.user as UserModel;
    try {
      const requestBoxes = await new BoxRequest()
        .filter({ user: userObj.id })
        .sort('created_at', 'DESC')
        .include(['info'])
        .all();

      const styledTypeBoxRequests = requestBoxes.filter((requestBox: BoxRequest) => requestBox.type === 'styled_box');

      if (styledTypeBoxRequests.length) {
        this.latestBoxRequest = styledTypeBoxRequests[0];
        this.setLatestBoxRequestAnswers();
      } else {
        this.convertCheckboxAnswersToText();
      }
    } catch (error) {
      // Handle error
      console.log('error: ', error);
      return;
    }
  }

  protected convertCheckboxAnswersToText() {
    this.stijlquizAnswers.forEach((currentAnswer) => {
      if (
        currentAnswer.key === 'radio-list-boxes_4' || currentAnswer.key === 'checkbox-list_wishlist'
      ) {
        if (isArray(currentAnswer.value)) {
          currentAnswer.value = currentAnswer.value.map((obj: Record<string, any>) => obj.label).join(', ');
          return;
        }

        if (Object.keys(currentAnswer.value).length) {
          const objectValue = currentAnswer.value;
          currentAnswer.value = '';
          currentAnswer.value = objectValue as string;
          return;
        }
      }
    });
  }

  protected setLatestBoxRequestAnswers(): void {
    if (!this.latestBoxRequest) {
      return;
    }
    if (!this.latestBoxRequest.info) {
      return;
    }

    const hasRelevantInfo = this.latestBoxRequest.info.some((info: Record<string, any>) => {
      return (
        info.key === 'Waar ben je op zoek naar in deze box?' ||
        info.key === 'Zijn er specifieke items die je graag zou willen ontvangen?' ||
        info.key === 'Box voorkeuren'
      );
    });

    if (!hasRelevantInfo) {
      this.convertCheckboxAnswersToText();
      return;
    }

    this.latestBoxRequest.info.forEach(item => {
      this.stijlquizAnswers.forEach(currentAnswer => {
        if (item.key === 'Box voorkeuren') {
          if (currentAnswer.question && currentAnswer.question.key === 'textarea_1' && currentAnswer.question.title === 'Box voorkeuren') {
            currentAnswer.value = item.value || '-'
          }
        }

        if (currentAnswer.question && currentAnswer.question.questionText === item.key) {
          currentAnswer.value = item.value || '-';
        }
      });
    });
  }

  protected onPhoneInput(phoneInput: PhoneNumber) {
    this.phone = phoneInput.number;
    this.phoneValid = phoneInput.isValid;
  }

  protected initBreadcrumb() {
    this.$root.$emit('breadcrumbUpdated', {
      crumb: [{ name: this.$t('navigation.users'), path: '/users' }, { name: (this.user as any).name }],
    });
  }

  protected get countries() {
    return filtered_country_list;
  }

  private clearSaved() {
    setTimeout(() => {
      this.isUpdating = false;
      this.isSaved = false;
      this.isEditingProfile = false;
      this.isEditingAddress = false;
      this.isEditingProfile = false;
      this.isEditingRepeat = false;
      this.isEditingRepeatFrequency = false;
      this.isEditingSettings = false;
      this.isSavedNotes = false;
      this.isEditingNotes = false;
      this.isUpdatingNotes = false;
      this.isSavedNote = false;
      this.isEditingNote = false;
      this.isUpdatingNote = false;
    }, 1200);
  }

  protected resetUser() {
    if (!this.userClone) {
      return;
    }

    this.user = cloneDeep(this.userClone);
    this.userClone = null;

    this.isChangingPassword = false;
    this.resetMiPassword();
  }

  protected canSubmit(password: string, confirmation: string) {
    if (!password || !confirmation) {
      return false;
    }
    return password === confirmation;
  }

  protected upgradeToContact() {
    new Lead()
      .upgradeToContact((this.user as Lead).id || '')
      .then(() => {
        this.$store.dispatch('openDialog', this.upgradeDialogOptions);
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  private get upgradeDialogOptions(): Options {
    return {
      title: 'Contact succesvol aangemaakt',
      text: 'Deze Lead is succesvol geconverteerd naar Contact',
      type: 'success',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.confirm').toString(),
        },
      },
    };
  }

  protected changeOrUpdatePassword() {
    if (this.isChangingPassword) {
      this.changePassword();
      this.isLoadingButton = true;
    } else {
      this.isChangingPassword = true;
    }
  }

  protected get canSavePassword() {
    if (!this.isChangingPassword) {
      return true;
    }

    if (this.isChangingPassword && this.isPasswordValid) {
      return true;
    }

    return false;
  }

  private changePassword() {
    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;
    if (!miPassword) {
      return;
    }

    const password = miPassword.getPassword().password;
    const password_confirmation = miPassword.getPassword().confirmation;

    if (!this.canSubmit(password, password_confirmation)) {
      return;
    }

    this.isLoadingButton = true;
    this.user
      .update({ password, password_confirmation })
      .then(() => {
        this.isDelayingButtonFeedback();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected cancelEditPassword() {
    this.resetMiPassword();
    this.isChangingPassword = false;
  }

  protected get canChangePassword() {
    if (!this.user) {
      return false;
    }

    if (!this.$store.state.Auth) {
      return false;
    }

    // You can change your own password
    if ((this.user as any).id === this.$store.state.Auth.id) {
      return true;
    }

    // An admin can always reset the password
    return this.isAdmin || this.isRoot;
  }

  protected update() {
    this.isUpdating = true;
    if (!this.user) {
      return;
    }


    (this.user as Lead).answers = JSON.stringify((this.user as Lead).answers);
    this.user
      .update()
      .then(() => {
        this.isSaved = true;
        this.clearSaved();
        this.initialize();
      })
      .catch((error: AxiosError) => {
        this.clearSaved();
        this.initialize();
        ErrorHandler.network(error);
      });
  }

  protected updateAdminOrPlannerNote() {
    this.isUpdatingNote = true;
    if (!this.userNotes) {
      return;
    }

    const payload = {
      note: this.userAdminOrPlannerNote,
    };

    this.user
      .include('profile')
      .update(payload)
      .then(() => {
        this.isSavedNote = true;
        this.userAdminOrPlannerNote = '';
        this.clearSaved();
        this.initialize();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        this.isUpdatingNote = false;
      });
  }

  protected updateNotes() {
    this.isUpdatingNotes = true;
    if (!this.userNotes) {
      return;
    }

    const payload = {
      profile: {
        note: this.userNotes,
      },
    };

    this.user
      .include('profile')
      .update(payload)
      .then(() => {
        this.isSavedNotes = true;
        this.userNotes = '';
        this.clearSaved();
        this.initialize();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        this.isUpdatingNotes = false;
      });

    // if(this.address && this.address.id) {
    //   this.address.contact = this.user.contactId || '';

    //   if(typeof this.address.country !== 'string') {
    //     this.address.country = (this.address.country as Country).id;
    //   }

    //   new Address(this.address)
    //   .update()
    //   .then(() => {
    //     callCount -= 1;
    //     if(callCount <= 0) {
    //       this.isSaved = true;
    //       this.clearSaved();
    //       this.initialize();
    //     }
    //   })
    //   .catch((error: AxiosError) => {
    //     ErrorHandler.network(error);
    //   });
    // }
    // else if(this.address) {
    //   this.address.contact = this.user.contactId || '';
    //   new Address(this.address)
    //   .create()
    //   .then(() => {
    //     callCount -= 1;
    //     if(callCount <= 0) {
    //       this.initialize();
    //     }
    //   })
    //   .catch((error: AxiosError) => {
    //     ErrorHandler.network(error);
    //   });
    // }
  }

  protected isDelayingButtonFeedback() {
    setTimeout(() => {
      this.isLoadingButton = false;
      this.isSavedButton = true;
      this.isChangingPassword = false;

      setTimeout(() => {
        this.isSavedButton = false;
      }, 800);
    }, 1200);
  }

  private resetMiPassword() {
    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;
    if (miPassword) {
      miPassword.reset();
    }
  }

  protected async handleAnswerUpdate(answers: any) {
    this.$set(this.user, 'answers', answers);
    if (this.latestBoxRequest) {
      await this.updateLatestBoxRequestAnswers(answers);
    }
    this.update();
  }

  protected handleImageUpdate(images: any[]) {
    let callCount = images.length;

    images.forEach((file: string) => {
      (this.userFile as LeadFile | UserFile)
        .user((this.user as any).id)
        .create({ file })
        .then(() => {
          callCount -= 1;

          if (callCount <= 0) {
            this.fetchFiles();
          }
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    });
  }

  protected fetchFiles() {
    (this.userFile as LeadFile | UserFile)
      .user((this.user as any).id)
      .limit(100)
      .all()
      .then((images: any) => {
        this.stijlquizImages = images;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected fetchOrders() {
    new Order()
      .filter({ user: this.$route.params.id, status: 'draft' })
      .all()
      .then((response: any) => {
        this.currentOrders = response;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected handleImageDelete(fileId: string) {
    (this.userFile as LeadFile | UserFile)
      .deleteUserFile((this.user as any).id, fileId)
      .then(() => {
        this.fetchFiles();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async createOrReturn(): Promise<void> {
    if (this.isBlacklisted || this.hasOpenInvoice || this.hasTooManyActiveOrders || this.hasOrderOnDispatched) {
      return;
    }

    const user = this.user as UserModel;

    this.inProgress = null;
    const inProgress: BoxRequest | null = await new BoxRequest().getInProgress(user.id);

    if (inProgress && inProgress.id) {
      this.inProgress = inProgress;
      this.$store.dispatch('openDialog', this.createBoxRequestInProgressDialog);
      return;
    }

    this.isEditingCurrentOrder ? this.returnToOrder() : this.createOrder();
  }

  protected returnToOrder() {
    this.$router.go(-1);
  }

  protected createOrder() {
    const orderPayload = {
      user: this.$route.params.id,
    };
    new Order()
      .create(orderPayload)
      .then((response: any) => {
        this.$router.push({
          name: 'webshop.order',
          params: { id: response.id },
        });
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected isCreatingPaazlOrderDialog() {
    this.$store.dispatch('openDialog', this.dialogOptionsCreatePaazl);
  }

  protected createPaazlOrder() {
    if (!this.user || !(this.user as any).id) {
      return;
    }

    new Contact()
      .createPaazlOrder((this.user as any).id, {})
      .then((user: any) => {
        this.user = user.data;
        this.userClone = null;
        if ((this.user as any).profile) {
          (this.user as any).answers = JSON.parse(user.data.profile.answers);
        }

        if (((this.user as any).profile as any).paazl) {
          this.paazlOrders = ((this.user as any).profile as any).paazl;
        }

        this.stijlquizAnswers = user.answers;
        this.$store.dispatch('updateLeadAnswers', this.user);
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected formattedEventDate(date: string) {
    if (!date) {
      return 'dd-mm-jjj';
    }
    return formatDate(date);
  }

  protected get dialogOptionsCreatePaazl(): Options {
    return {
      title: 'Bevestiging',
      text: `Weet je zeker dat je een Paazl order wilt aanmaken?`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Creëer order',
          action: () => {
            this.createPaazlOrder();
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        },
      },
    };
  }

  protected get attachmentHeaders() {
    return [
      { text: 'Bestandsnaam', sortable: false, value: 'name', align: 'left' },
      { text: 'URL', value: 'url', sortable: false },
      { text: 'Aangemaakt op', value: 'created_at', sortable: false },
    ];
  }

  private get createBoxRequestInProgressDialog(): Options {
    return {
      title: i18n.tc('dialogOptions.existingOrderFound').toString(),
      text: i18n.tc('dialogOptions.existingOrderFoundForClient').toString(),
      type: 'warning',
      buttons: {
        confirm: {
          text: i18n.tc('dialogOptions.showOrder').toString(),
          action: (): void => {
            if (this.inProgress) {
              window.location.href = `/aanvragen/${this.inProgress.id}`;
            }
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        },
      },
    };
  }

  protected get passwordButtonText() {
    if (this.isChangingPassword) {
      return this.$t('profile.password.save').toString();
    }

    if (this.isSavedButton) {
      return this.$t('profile.password.saved').toString();
    }

    return this.$t('profile.password.change').toString();
  }

  protected get passwordButtonIcon() {
    if (this.isChangingPassword) {
      return 'save';
    }
    if (this.isSavedButton) {
      return 'check';
    }
    return 'lock';
  }

  protected get passwordButtonColor() {
    if (this.isChangingPassword) {
      return 'primary';
    }
    if (this.isSavedButton) {
      return 'success';
    }
    return 'warning';
  }

  protected get userAvatar() {
    if ((this.user as any).avatar && !(this.user as any).avatar.includes('gravatar')) {
      return (this.user as any).avatar;
    }

    return new Avatar((this.user as any).name ? (this.user as any).name : 'NA', {
      color: styles['white'],
      background: styles['primary'],
      size: 512,
    }).toDataUrl();
  }

  protected get showLeadContent() {
    return this.type && (this.type === 'Leads' || this.type === 'Contacts');
  }

  protected get canEditStatus() {
    if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
      return false;
    }

    return this.$store.state.Auth.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR]);
  }

  protected get isBlacklisted() {
    // @ts-ignore
    if (!this.user || !this.user.profile) {
      return false;
    }

    // @ts-ignore
    return this.user.profile.status === 'blacklist';
  }

  protected get hasTooManyActiveOrders() {
    // @ts-ignore
    if (!this.user || !this.user.rights) {
      return false;
    }

    // @ts-ignore
    return this.user.rights.has_too_many_active_orders;
  }

  protected get hasOrderOnDispatched() {
    const user = this.user as UserModel;
    // @ts-ignore
    if (!user || !user.rights) {
      return false;
    }

    if (user && user.profile && user.profile.user_id === '114451') {
      return false;
    }

    // @ts-ignore
    return user.rights.has_order_on_dispatched;
  }

  protected get hasOpenInvoice() {
    // @ts-ignore
    if (!this.user || !this.user.profile) {
      return false;
    }

    // @ts-ignore
    return this.user.profile.status === 'open_invoice';
  }

  protected get leadHasCreditRisk() {
    // @ts-ignore
    if (!this.user || !this.user.credit_check || !this.user.credit_check.ecommercescore) {
      return false;
    }

    // @ts-ignore
    if (
      !this.$store.state ||
      !this.$store.state.Auth ||
      !this.$store.state.Auth.hasRole([RoleEnum.STYLIST, RoleEnum.SUPPORT, RoleEnum.ADMINISTRATOR])
    ) {
      return false;
    }

    // @ts-ignore
    return this.user.credit_check.ecommercescore <= 800;
  }

  protected get contactHasCreditRisk() {

    if (
      !this.user ||
      // @ts-ignore
      !this.user.profile ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check.ecommercescore
    ) {
      return false;
    }

    // @ts-ignore
    if (
      !this.$store.state ||
      !this.$store.state.Auth ||
      !this.$store.state.Auth.hasRole([RoleEnum.STYLIST, RoleEnum.SUPPORT, RoleEnum.ADMINISTRATOR])
    ) {
      return false;
    }

    // @ts-ignore
    return this.user.profile.credit_check.ecommercescore <= 800;
  }

  protected get isStylist() {
    return !!this.$store.state.Auth && !!this.$store.state.Auth.hasRole([RoleEnum.STYLIST], false);
  }

  protected get canViewUserOrOrderDetails() {
    if (!this.$store.state.Auth) {
      return false;
    }

    return !!this.$store.state.Auth.canViewUserOrOrderDetails();
  }

  protected get isRoot() {
    return !!this.$store.state.Auth.hasRole([RoleEnum.ROOT]);
  }

  protected get isAdmin() {
    return !!this.$store.state.Auth.hasRole([RoleEnum.ADMINISTRATOR]);
  }

  protected get isSupport() {
    return !!this.$store.state.Auth.hasRole([RoleEnum.SUPPORT]);
  }

  protected get leadShowEcommerceScore() {
    // @ts-ignore
    if (!this.user || !this.user.credit_check || !this.user.credit_check.ecommercescore) {
      return false;
    }

    // @ts-ignore
    if (!this.$store.state || !this.$store.state.Auth || !this.$store.state.Auth.hasRole([RoleEnum.ROOT])) {
      return false;
    }

    return true;
  }

  protected get contactShowEcommerceScore() {
    if (
      !this.user ||
      // @ts-ignore
      !this.user.profile ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check.ecommercescore
    ) {
      return false;
    }

    // @ts-ignore
    if (!this.$store.state || !this.$store.state.Auth || !this.$store.state.Auth.hasRole([RoleEnum.ROOT])) {
      return false;
    }

    return true;
  }

  protected get leadEcommerceScore() {
    // @ts-ignore
    if (!this.user || !this.user.credit_check || !this.user.credit_check.ecommercescore) {
      return 0;
    }

    // @ts-ignore
    return this.user.credit_check.ecommercescore;
  }

  protected get leadEcommerceScoreIsLegit() {
    // @ts-ignore
    if (!this.user || !this.user.credit_check || !this.user.credit_check.is_legit) {
      return false;
    }

    // @ts-ignore
    return this.user.credit_check.is_legit;
  }

  protected get stylequizDate() {
    // @ts-ignore
    if (!this.user || !this.user.profile || this.user.profile.answers_updated_at === '') {
      return null;
    }

    // @ts-ignore
    return formatDate(this.user.profile.answers_updated_at, 'dd-LL-yyyy');
  }

  protected get contactEcommerceScore() {
    if (
      !this.user ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check.ecommercescore
    ) {
      return 0;
    }

    // @ts-ignore
    return this.user.profile.credit_check.ecommercescore;
  }

  protected get contactEcommerceScoreIsLegit() {
    if (
      !this.user ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check ||
      // @ts-ignore
      !this.user.profile.credit_check.is_legit
    ) {
      return false;
    }

    // @ts-ignore
    return this.user.profile.credit_check.is_legit;
  }

  // Only show for yourself
  protected get showMfa() {
    // @ts-ignore
    if (!this.user || !this.$store || !this.$store.state || !this.$store.state.Auth) {
      return false;
    }

    // @ts-ignore
    return this.user.id === this.$store.state.Auth.id;
  }

  protected get selectedCountryCode() {
    const selected = this.languageOptions.find(
      (lang) => lang.value === (this.user as Contact).language
    );
    return selected ? selected.text : '';
  };


  get tableOptions() {
    return {
      name: {
        singular: 'Order',
        plural: 'Orders',
      },
      sort: {
        key: 'created_at',
        order: 'DESC',
      },
      headers: [
        {
          text: this.$tc('tableHeaders.orderNumber.title'),
          value: 'peoplevox_id',
          class: 'linkable',
          sortable: {
            key: 'status',
            order: 'ASC',
          },
          transform: (value: string) => {
            return value ? value : '-';
          },
          action: '/webshop/order/{id}',
        },
        {
          text: this.$tc('tableHeaders.type.title'),
          value: 'box_type',
          sortable: {
            key: 'box_type',
            order: 'ASC',
          },
          transform: (value: string) => {
            const styledBox = styledBoxOptions.find((styledBox: StyledBoxOptions) => {
              return value === styledBox.value;
            });
            if (typeof styledBox !== 'undefined') {
              return (styledBox as StyledBoxOptions).text;
            } else {
              return '';
            }
          },
        },
        {
          text: this.$tc('tableHeaders.status.title'),
          value: 'status',
          transform: (value: string) => {
            const status = statusOptions.find((status: StatusOptions) => {
              return value === status.value;
            });

            if (!status) {
              return;
            }

            return `<span class="statusPill statusPill--${(status as StatusOptions).value}">${(status as StatusOptions).text
              }</span>`;
          },
          sortable: {
            key: 'status',
            order: 'ASC',
          },
        },
        {
          text: this.$tc('tableHeaders.finalized.title'),
          value: 'finalized_at',
          class: 'text-right',
          sortable: {
            key: 'finalized_at',
            order: 'ASC',
          },
          transform: (date: string) => {
            return this.dateTime(date);
          },
        },
        {
          text: this.$tc('tableHeaders.returned.title'),
          value: 'returned_at',
          class: 'text-right',
          sortable: {
            key: 'returned_at',
            order: 'ASC',
          },
          transform: (date: string) => {
            return this.dateTime(date);
          },
        },
        {
          text: this.$tc('tableHeaders.netSales.title'),
          value: 'total_without_vat',
          sortable: {
            key: 'total_without_vat',
            order: 'ASC',
          },
          transform: (value: number) => {
            return value ? currency(value) : '';
          },
        },
        {
          text: this.$tc('tableHeaders.quantity.title'),
          value: 'quantity',
          class: 'text-right',
          sortable: {
            key: 'quantity',
            order: 'ASC',
          },
        },
        {
          text: this.$tc('tableHeaders.understanding.title'),
          value: 'evaluation',
          class: 'text-right',
          transform: (questions: any) => {
            return questions['q4_begrip'] ? questions['q4_begrip'] : '';
          },
        },
        {
          text: this.$tc('tableHeaders.stylist.title'),
          value: 'stylist_name',
          sortable: {
            key: 'stylist_name',
            order: 'ASC',
          },
        },
      ],
      actions: [],
      model: new Order()
        .include([
          'product',
          'evaluation',
          'kept-items',
          'returned-items',
          'attachments',
          'color',
          'size',
          'brand',
          'supplier',
        ])
        .filter({
          user: this.user,
          boxTypeIn: ['styled_box', 'swap_box', 'swipe_to_shop', 'shopify', 'after_sales'],
          statusIn: ['completed', 'invoiced', 'checkout'],
        }),
    };
  }

  protected assignAsPlanner() {
    if (!this.user) {
      return;
    }

    // @ts-ignore
    this.user.assignAsPlanner().then(() => {
      // @ts-ignore
      this.user.is_planner = true;
    });
  }

  protected removeAsPlanner() {
    if (!this.user) {
      return;
    }

    // @ts-ignore
    this.user.removeAsPlanner().then(() => {
      // @ts-ignore
      this.user.is_planner = false;
    });
  }

  protected get userIsPlanner(): boolean {
    if (!this.user) {
      return false;
    }

    // @ts-ignore
    return this.user.is_planner === true;
  }

  protected get isAdminOrPlanner(): boolean {
    return this.isAdmin || this.userIsPlanner;
  }

  protected get canBePlanner(): boolean {
    return this.type === 'Stylists' || this.type === 'Admin' || this.type === 'Support';
  }

  protected dateTime(date: string) {
    if (date === '0000-00-00 00:00:00') {
      return 'N/A';
    } else {
      return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
    }
  }


  @Watch('isEditingProfile')
  protected editingChanged(isEditing: boolean) {
    if (isEditing) {
      this.userClone = cloneDeep(this.user);
    }
  }

  @Watch('$route')
  public routeChanged() {
    this.iterationKey += 1;
    this.initialize();
  }
}

interface inputElement {
  value: string | number;
}

interface paazlOrder {
  created_at: string;
  reference_number: string;
}

interface PhoneNumber {
  number: string;
  isValid: boolean;
  country: object;
}

type profileSection = 'profile' | 'repeat' | 'repeat_frequency' | 'address' | 'settings';
